import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo } from 'react'
import { Spinner } from '@phosphor-icons/react'
import { AxiosError } from 'axios'
import { Button, Input, Form, FormControl, FormField, FormItem, FormMessage, Checkbox, FormLabel } from '@/components/ui'
import { useSignUpMutation } from '@/feature/auth/api'
import { cn } from '@/lib/utils'
import { PasswordInput } from '@/components/input/PasswordInput'
import { LogoExtended } from '@/routes/layouts/public-layout'
import { Show } from '@/components/shared/show'

export const Welcome = () => {
  const { t } = useTranslation(`wizard`)
  const { t: commonT } = useTranslation(`common`)
  const schema = useMemo(
    () =>
      z.object({
        email: z.string().email(t(`sign_up.screens.welcome.form.inputs.email.error.invalid`)),
        password: z.string().min(8, t(`sign_up.screens.welcome.form.inputs.password.helper_text`)).max(32),
        clone_anyway: z.boolean().optional()
      }),
    [t]
  )
  type FormValues = z.infer<typeof schema>

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: ``,
      password: ``,
      clone_anyway: false
    }
  })
  const navigate = useNavigate()
  const { mutate, isPending, isSuccess, isError, error } = useSignUpMutation()

  const submitFn = useCallback(
    (data: FormValues) => {
      mutate(data)
    },
    [mutate]
  )

  const paswordState = useMemo(() => {
    if (form.formState.isValid) {
      return `text-success`
    }
    if (form.formState.errors.password) {
      return `text-destructive`
    }
    return `text-muted-foreground`
  }, [form.formState.errors.password, form.formState.isValid])

  useEffect(() => {
    if (isSuccess) {
      navigate(`/auth/resend`)
    } else if (isError) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.detail
        const duplicateEmail = errorMessage?.includes(`already exists`)
        form.setError(`email`, {
          message: duplicateEmail ? `User already exists` : errorMessage
        })
      } else {
        form.setError(`root`, {
          message: `There was an error, try again later`
        })
      }
    }
  }, [isSuccess, navigate, isError, form, t, error])

  return (
    <div className='flex h-full w-full flex-col items-center justify-between bg-white p-8'>
      <LogoExtended />
      <div className='w-full max-w-md'>
        <h1 className='text-center text-4xl font-medium'>{t(`sign_up.screens.welcome.title`)}</h1>
        <p className='mx-auto max-w-sm text-center text-lg text-muted-foreground'>{t(`sign_up.screens.welcome.description`)}</p>
        <Form {...form}>
          <form className='mt-10 flex w-full flex-col gap-4' onSubmit={form.handleSubmit(submitFn)}>
            {form.formState.errors.root ? (
              <FormMessage className='text-center text-destructive'>{form.formState.errors.root.message}</FormMessage>
            ) : null}
            <FormField
              name='email'
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      autoComplete='email'
                      placeholder='Email'
                      className={cn(`rounded border`, form.formState.errors.email ? `border-destructive` : `border-gray-300`)}
                      {...field}
                      onChange={e => {
                        field.onChange({
                          ...e,
                          target: {
                            ...e.target,
                            value: e.target.value.trim()
                          }
                        })
                      }}
                    />
                  </FormControl>
                  <FormMessage className='text-destructive' />
                </FormItem>
              )}
            />
            <FormField
              name='password'
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PasswordInput placeholder={t(`sign_up.screens.welcome.form.inputs.password.placeholder`)} {...field} />
                  </FormControl>
                  <FormMessage className={paswordState}>{t(`sign_up.screens.welcome.form.inputs.password.helper_text`)}</FormMessage>
                </FormItem>
              )}
            />
            <Show when={import.meta.env.NODE_ENV !== `production` || import.meta.env.DEV}>
              <FormField
                name='clone_anyway'
                control={form.control}
                render={({ field }) => (
                  <FormItem className='flex items-center gap-2'>
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel
                      className='p-0'
                      style={{
                        marginTop: `0px`
                      }}
                    >
                      {t(`sign_up.screens.welcome.form.inputs.clone_anyway.label`)}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </Show>
            <Button size='lg' type='submit' disabled={form.formState.isSubmitting || !form.formState.isValid || isPending}>
              {isPending ? <Spinner className='h-4 w-4 animate-spin' size={20} /> : commonT(`button.sign_up`)}
            </Button>
          </form>
        </Form>
      </div>
      <footer className='text-center text-sm'>
        <div className='flex items-center justify-center gap-1'>
          <p className='text-muted-foreground'>{t(`sign_up.screens.welcome.login.text`)}</p>
          <Link to='/auth/login' className='primary-link'>
            {t(`sign_up.screens.welcome.login.button`)}
          </Link>
        </div>
        <div className='mt-2 flex items-center justify-center gap-1'>
          <p className='text-muted-foreground'>{t(`sign_up.screens.welcome.term_of_use`)}</p>
          <Link to='/privacy-policy' className='primary-link'>
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  )
}
